import type { TranslateService } from '@ngx-translate/core';
import { SearchGlobalModel } from '@models/search-global.model';
import { ZendeskArticleModel } from '@models/zendesk-article.model';
import CategoryId = SearchGlobalModel.Common.CategoryId;

export class SearchGlobalModalHelper {
  static toBooleanParam(value: unknown): boolean {
    return value === 'true' || value === true;
  }

  static normalizeCategoryToken(token: string, operator: string): string {
    if (!token?.startsWith(operator)) return token;
    return token.substring(operator.length);
  }

  static isCategoryToken(token: string, operator: string): boolean {
    return !!token && token.startsWith(operator) && token.length > operator.length;
  }

  static ensureCategoryId(value: string, all: CategoryId[]): CategoryId | null {
    const v = (value || '').toLowerCase() as CategoryId;
    return (all.includes(v) ? v : null) as CategoryId | null;
  }

  static getTypeI18nKey(type: string): string {
    switch (type) {
      case 'camera':
        return 'SEARCH_GLOBAL_MODAL.CAMERA';
      case 'event':
        return 'SEARCH_GLOBAL_MODAL.EVENT';
      case 'wall':
        return 'SEARCH_GLOBAL_MODAL.WALL';
      case 'person':
        return 'SEARCH_GLOBAL_MODAL.PERSON';
      case 'sublocation':
        return 'SEARCH_GLOBAL_MODAL.SUB_LOCATION';
      case 'speaker':
        return 'SEARCH_GLOBAL_MODAL.SPEAKER';
      case 'location':
        return 'SEARCH_GLOBAL_MODAL.LOCATION';
      case 'core':
        return 'SEARCH_GLOBAL_MODAL.CORE';
      case 'alarm':
        return 'SEARCH_GLOBAL_MODAL.ALARM';
      case 'page':
        return 'SEARCH_GLOBAL_MODAL.PAGE';
      case 'article':
        return 'SEARCH_GLOBAL_MODAL.DOCS';
      case 'building':
        return 'SEARCH_GLOBAL_MODAL.BUILDING';
      case 'floor':
        return 'SEARCH_GLOBAL_MODAL.FLOOR';
      default:
        return type;
    }
  }

  static highlightMatch(text: string, searchTerm: string): string {
    if (!searchTerm || !text) return text;
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
    return text.toString().replace(regex, '<mark class="search-highlight">$1</mark>');
  }

  static highlightByIndices(text: string, indices?: [number, number][], isHighlightEnabled: boolean = true): string {
    if (!isHighlightEnabled) return text;
    if (!text || !indices || indices.length === 0) return text;
    let highlightedText = text;
    const sorted = [...indices].sort((a, b) => b[0] - a[0]);
    sorted.forEach(([start, end]) => {
      const before = highlightedText.substring(0, start);
      const matched = highlightedText.substring(start, end + 1);
      const after = highlightedText.substring(end + 1);
      highlightedText = before + '<mark class="search-highlight">' + matched + '</mark>' + after;
    });
    return highlightedText;
  }

  static getCategoryLocalizedLabel(translate: TranslateService, categoryId: CategoryId): string {
    return translate.instant(SearchGlobalModalHelper.getTypeI18nKey(categoryId)) || categoryId;
  }

  static getFieldLabels(translate: TranslateService): { [key: string]: string } {
    return {
      subInfo: translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.ADDITIONAL_INFO'),
      'raw.city': translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.CITY'),
      'raw.address': translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.ADDRESS'),
      'raw.description': translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.DESCRIPTION'),
      'raw.retentionDays': translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.RETENTION_PERIOD'),
      'raw.ipAddress': translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.IP_ADDRESS'),
      'raw.cameras.cameraName': translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.CONTAINS_CAMERA_NAMED'),
      'raw.edges.edgeName': translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.CONTAINS_EDGE_NAMED'),
      'raw.locations.locationName': translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.CONTAINS_LOCATION_NAMED'),
      'raw.subLocations.subLocationName': translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.CONTAINS_SUBLOCATION_NAMED'),
      'raw.speakers.speakerName': translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.CONTAINS_SPEAKER_NAMED'),
      'raw.floors.floorName': translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.CONTAINS_FLOOR_NAMED'),
      'raw.buildings.buildingName': translate.instant('SEARCH_GLOBAL_MODAL.MATCH_FIELDS.CONTAINS_BUILDING_NAMED'),
    };
  }

  static getMatchReason(result: any, translate: TranslateService): string | null {
    if (!result?.matches?.length) return null;
    const primaryMatch = result.matches[0];
    const fieldLabels = SearchGlobalModalHelper.getFieldLabels(translate);
    const label = fieldLabels[primaryMatch.key];
    if (!label) return null;
    return `${label}: "${primaryMatch.value}"`;
  }

  static getAllMatchReasons(result: any, translate: TranslateService): string[] {
    if (!result?.matches?.length) return [];
    const fieldLabels = SearchGlobalModalHelper.getFieldLabels(translate);
    return result.matches
      .filter((m: any) => fieldLabels[m.key])
      .map((m: any) => `${fieldLabels[m.key]}: "${m.value}"`);
  }

  static highlightMatchReasonValue(matchReason: string, searchTerm: string): string {
    if (!searchTerm || !matchReason) return matchReason;
    const colonIndex = matchReason.indexOf(':');
    if (colonIndex === -1) return SearchGlobalModalHelper.highlightMatch(matchReason, searchTerm);
    const label = matchReason.substring(0, colonIndex + 2);
    const value = matchReason.substring(colonIndex + 2);
    const highlightedValue = SearchGlobalModalHelper.highlightMatch(value, searchTerm);
    return label + highlightedValue;
  }

  static groupMatchReasons(result: any, translate: TranslateService): { [key: string]: string[] } {
    if (!result?.matches) return {};
    const fieldLabels = SearchGlobalModalHelper.getFieldLabels(translate);
    const groups: { [key: string]: string[] } = {};
    result.matches.forEach((match: any) => {
      if (fieldLabels[match.key]) {
        const label = fieldLabels[match.key];
        if (!groups[label]) groups[label] = [];
        groups[label].push(match.value);
      }
    });
    return groups;
  }

  static groupMatchReasonsWithHighlighting(
    result: any,
    translate: TranslateService,
    searchTerm: string,
    isHighlightEnabled: boolean = true,
  ): { [key: string]: string[] } {
    if (!result?.matches) return {};
    const fieldLabels = SearchGlobalModalHelper.getFieldLabels(translate);
    const groups: { [key: string]: string[] } = {};
    result.matches.forEach((match: any) => {
      if (fieldLabels[match.key]) {
        const label = fieldLabels[match.key];
        if (!groups[label]) groups[label] = [];
        const highlightedValue = SearchGlobalModalHelper.highlightByIndices(
          match.value,
          match.indices,
          isHighlightEnabled,
        );
        groups[label].push(`"${highlightedValue}"`);
      }
    });
    return groups;
  }

  static getMatchReasonWithHighlighting(
    result: any,
    translate: TranslateService,
    isHighlightEnabled: boolean = true,
  ): string {
    if (!result?.matches?.length) return '';
    const fieldLabels = SearchGlobalModalHelper.getFieldLabels(translate);
    const firstMatch = result.matches[0];
    const label = fieldLabels[firstMatch.key];
    if (!label) return '';
    const highlightedValue = SearchGlobalModalHelper.highlightByIndices(
      firstMatch.value,
      firstMatch.indices,
      isHighlightEnabled,
    );
    return `${label}: "${highlightedValue}"`;
  }

  static sortCategoriesForFragment(
    translate: TranslateService,
    fragment: string,
    exclude: string[],
    available: CategoryId[],
  ): CategoryId[] {
    const raw = (fragment || '').toLowerCase();
    const excluded = new Set(exclude);
    const pool = available.filter((c) => !excluded.has(c));
    if (!raw) return [...pool].sort();
    const scored = pool
      .map((id) => {
        const idLc = id.toLowerCase();
        const labelLc = SearchGlobalModalHelper.getCategoryLocalizedLabel(translate, id).toLowerCase();
        let bestScore: number | null = null;
        let bestPos = 0;
        const evalCandidate = (candidate: string) => {
          if (candidate === raw) {
            bestScore = 0;
            bestPos = 0;
            return;
          }
          const pos = candidate.indexOf(raw);
          if (pos === 0) {
            if (bestScore === null || 1 < bestScore || (bestScore === 1 && pos < bestPos)) {
              bestScore = 1;
              bestPos = pos;
            }
          } else if (pos > 0) {
            if (bestScore === null || 2 < bestScore || (bestScore === 2 && pos < bestPos)) {
              bestScore = 2;
              bestPos = pos;
            }
          }
        };
        evalCandidate(idLc);
        evalCandidate(labelLc);
        if (bestScore === null) return null;
        return { c: id, score: bestScore, pos: bestPos };
      })
      .filter((x: any) => !!x) as Array<{ c: CategoryId; score: number; pos: number }>;
    scored.sort((a, b) => a.score - b.score || a.pos - b.pos || a.c.length - b.c.length);
    return scored.map((s) => s.c);
  }

  static stripInlineColor(html: string): string {
    if (!html) return '';
    // Remove color: ... from style attributes while preserving other styles
    html = html.replace(/style=(['")(]*)([^'"<>]*)(\1)/gi, (match, quote, styles) => {
      const cleaned = styles
        .replace(/(?:^|;)\s*color\s*:\s*[^;]+(?=;|$)/gi, '')
        .replace(/\s*;\s*;+/g, ';')
        .replace(/^\s*;|;\s*$/g, '')
        .trim();
      return cleaned ? `style=${quote}${cleaned}${quote}` : '';
    });
    return html;
  }

  static mapArticleToSearchItem(article: ZendeskArticleModel.ArticleItem): SearchGlobalModel.Items.ArticleItem {
    return {
      type: 'article',
      name: article.title,
      subInfo: article.category || '',
      icon: 'help-center',
      displayName: article.title,
      displayDescription: article.category || '',
      htmlUrl: article.htmlUrl,
      raw: {
        htmlUrl: article.htmlUrl,
        bodyHtml: article.bodyHtml,
        category: article.category,
        youtubeIds: article.youtubeIds || [],
        youtubeEmbedUrls: article.youtubeEmbedUrls || [],
      },
    };
  }
}
